'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import Axios from '@/lib/axiosConfig';
import { useAuth } from '@/context/Auth';

export interface ReviewLikeResponse {
  success: boolean;
  message: string | null;
  responseObject: unknown | null;
  statusCode: number;
}

interface ToggleArgs {
  reviewID: string;
  /** The state *before* the click — true means we are un-liking. */
  isLiked: boolean;
}

/**
 * Like / un-like a review.
 *
 * One endpoint, two verbs: POST to like, DELETE to un-like, both carrying
 * `{ reviewID }` in the body. Axios needs `data` (not a second positional
 * argument) to send a body on DELETE.
 *
 * The caller owns the optimistic flip — the cards keep their own count/liked
 * state the way the wishlist buttons do — so this only settles the server and
 * refreshes the lists afterwards.
 */
function useReviewLikeMutation(basePath: string, listKeys: string[]) {
  const { getAPIRequestTokenHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<ReviewLikeResponse, Error, ToggleArgs>({
    mutationFn: ({ reviewID, isLiked }) => {
      const headers = getAPIRequestTokenHeaders();
      return isLiked
        ? Axios.delete(basePath, { headers, data: { reviewID } }).then((r) => r.data)
        : Axios.post(basePath, { reviewID }, { headers }).then((r) => r.data);
    },
    onSuccess: () => {
      // Re-sync every list that can show this review, so a count updated here
      // is not stale in the preview list, the expanded list or the entity page.
      listKeys.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
    },
  });
}

export const useBusinessReviewLikeMutation = () =>
  useReviewLikeMutation('/businesses/reviews/likes', [
    'business-reviews',
    'business-reviews-infinite',
    'business',
  ]);

export const useEducationReviewLikeMutation = () =>
  useReviewLikeMutation('/educations/reviews/likes', [
    'education-reviews',
    'education-reviews-infinite',
    'education',
  ]);
